/**
 * moves — what a drag on the Practice Pulse board actually means.
 *
 * WHY THIS EXISTS (2026-10-30). PracticePulse.vue `onChange` recorded `moveFrom`/`selectedPatNum` only on
 * a `removed` event and `moveTo` only on an `added` event. Reordering cards INSIDE one column fires
 * neither, so `onEnd` reused the previous drag's values and wrote In-Treatment to Open Dental for the
 * last-dragged appointment. resolveDragMove is the single decision: no real move -> null -> write nothing.
 *
 * ⚠️ Keep this file import-free. The unit tests execute this exact file.
 */

function missing(v) {
  return v === null || v === undefined || v === '';
}

/**
 * @param {{from: (string|number|null), to: (string|number|null)}} move  column ids as vuedraggable
 *   reports them: 'checkin', 'checkout', or an OperatoryNum
 * @returns {null | {from, to, kind: 'seat'|'checkout'|'checkin'}} null when either side is unknown or
 *   the card stayed in its column (a reorder — never a write)
 */
export function resolveDragMove({ from, to } = {}) {
  if (missing(from) || missing(to)) return null;
  if (String(from) === String(to)) return null;

  let kind = 'seat';
  if (to === 'checkout') kind = 'checkout';
  else if (to === 'checkin') kind = 'checkin';

  return { from, to, kind };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Practice Ops page rules (2026-10-30, plan "Design review" → behaviour calls 1 and 2)
//
//   ONE PATIENT PER ROOM (call 1)   roomDropRefusal — a drop on an occupied room is refused with a toast.
//   NO READY GATE (call 2)          anyone in Check-in can be seated; nothing here asks about Ready.
//                                   (Classic PracticePulse.vue keeps its own gate; the new page overrides onMove.)
//   SEAT NEXT (tablet)              seatNext / seatNextLabel — the Check-in patient who has waited longest.
//
// These take the NORMALISED cards from board.js ({patNum, aptNum, scheduledMs, arrivedMs, first, last, name}).
// ─────────────────────────────────────────────────────────────────────────────────────────────────

const MS_PER_MINUTE = 60 * 1000;

function cleanText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function cardMs(value) {
  if (value instanceof Date) value = value.getTime();
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * "<Last>, <First>" for a card. `nameOf(card)` (optional — the page's roster lookup) may answer
 * { first, last } (or { FName, LName }), or a ready-made string; when it answers nothing the card's own
 * `last`/`first`, then its `name` ("First Last" — the classic card's display name; the LAST word is taken as
 * the surname, a best effort), then "Patient #<PatNum>". One name missing ⇒ just the other. Never throws.
 */
export function cardLastFirst(card, nameOf) {
  const c = card && typeof card === 'object' ? card : {};
  let looked = null;
  if (typeof nameOf === 'function') {
    try { looked = nameOf(c); } catch (e) { looked = null; }
  }

  const join = (last, first) => (last && first ? `${last}, ${first}` : (last || first));

  if (typeof looked === 'string' && cleanText(looked)) return cleanText(looked);
  if (looked && typeof looked === 'object') {
    const named = join(cleanText(looked.last || looked.LName), cleanText(looked.first || looked.FName));
    if (named) return named;
  }

  const own = join(cleanText(c.last), cleanText(c.first));
  if (own) return own;

  const full = cleanText(c.name);
  if (full) {
    if (full.indexOf(',') !== -1) return full;
    const words = full.split(/\s+/);
    if (words.length === 1) return full;
    return `${words[words.length - 1]}, ${words.slice(0, -1).join(' ')}`;
  }

  return c.patNum !== null && c.patNum !== undefined && c.patNum !== '' ? `Patient #${c.patNum}` : 'A patient';
}

/**
 * Is this room free for the patient being dropped on it?
 *
 * @param {Array} roomCards     the cards CURRENTLY in the target room (any number — old data and races can leave 2+)
 * @param {(string|number)} movingPatNum  the patient being moved
 * @param {string} roomLabel    the room's display name (settings.opLabels[opNum] or the OD name)
 * @param {Function} [nameOf]   see cardLastFirst
 * @returns {null|string} null = allowed; otherwise the toast text
 *   "<Room> is in use by <Last>, <First>. One patient per room."
 *
 * Allowed: an EMPTY room, and a room that holds only the moving patient (already there). Refused: a room with
 * anyone ELSE in it — including a legacy room of 2+ cards, where the first other patient is named. A reorder
 * inside one column never gets here: resolveDragMove already turned it into "no move".
 */
export function roomDropRefusal(roomCards, movingPatNum, roomLabel, nameOf) {
  const cards = Array.isArray(roomCards) ? roomCards.filter((c) => c && typeof c === 'object') : [];
  const other = cards.find((c) => String(c.patNum) !== String(movingPatNum));
  if (!other) return null;
  const room = cleanText(roomLabel) || 'That room';
  return `${room} is in use by ${cardLastFirst(other, nameOf)}. One patient per room.`;
}

/**
 * Whole minutes a Check-in card has waited at `nowMs` — the SAME rule as waitRules.js waitMin (clock starts at
 * the later of booked time and arrival, never negative) — or null when there is no live wait: a time is
 * unknown, or the patient is EARLY (arrived before the booked time and it is not yet that time).
 */
function liveWaitMin(card, nowMs) {
  const scheduled = cardMs(card.scheduledMs);
  const arrived = cardMs(card.arrivedMs);
  if (scheduled === null || arrived === null || !Number.isFinite(nowMs)) return null;
  if (arrived < scheduled && nowMs < scheduled) return null;
  return Math.max(0, Math.floor((nowMs - Math.max(scheduled, arrived)) / MS_PER_MINUTE));
}

function byArrival(a, b) {
  const aa = cardMs(a.arrivedMs);
  const bb = cardMs(b.arrivedMs);
  if (aa !== bb) {
    if (aa === null) return 1;
    if (bb === null) return -1;
    return aa - bb;
  }
  const sa = cardMs(a.scheduledMs);
  const sb = cardMs(b.scheduledMs);
  if (sa !== sb) {
    if (sa === null) return 1;
    if (sb === null) return -1;
    return sa - sb;
  }
  return (Number(a.aptNum) || 0) - (Number(b.aptNum) || 0);
}

/**
 * Who should be seated next: the Check-in card with the LONGEST live wait; a tie (same whole minutes) goes to
 * the earliest arrival. If nobody has a live wait yet (everyone is early, or times are missing) it is the
 * earliest arrival. null when Check-in is empty. NO Ready gate — a patient who is merely "Arrived" is a valid
 * answer. Returns the card itself.
 *
 * @param {Array} checkinCards  board.js cards of the Check-in column
 * @param {number} nowMs        the corrected "now" (clock.js) — waits are recomputed from the stored times
 */
export function seatNext(checkinCards, nowMs) {
  const cards = Array.isArray(checkinCards) ? checkinCards.filter((c) => c && typeof c === 'object') : [];
  if (cards.length === 0) return null;

  const waiting = cards
    .map((card) => ({ card, wait: liveWaitMin(card, nowMs) }))
    .filter((x) => x.wait !== null);

  if (waiting.length === 0) return cards.slice().sort(byArrival)[0];

  waiting.sort((x, y) => (y.wait - x.wait) || byArrival(x.card, y.card));
  return waiting[0].card;
}

/** The button label: "Seat next: Last, First", or "No one waiting" when seatNext found nobody. */
export function seatNextLabel(card, nameOf) {
  return card ? `Seat next: ${cardLastFirst(card, nameOf)}` : 'No one waiting';
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// planMove — the ONE decision behind a drop, the ⋯ menu, the phone's "Move to…" sheet and the tablet's "Seat next"
// (2026-10-30, chunk C). The Board, the Operatory view, the Phone view and the page all ask this, so the occupied-room
// refusal and the "a reorder writes nothing" rule are written once.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Where a board card is now, in the ids a move uses: 'checkin', 'checkout' or the OperatoryNum as a string. */
export function cardLocation(card) {
  const c = card && typeof card === 'object' ? card : {};
  if (c.column === 'room') return String(c.opNum);
  return c.column === 'checkout' ? 'checkout' : 'checkin';
}

/**
 * @param {{card: object, to: (string|number), rooms: Array}} input  `rooms` = board.js rooms ({opNum, label, cards})
 * @param {Function} [nameOf]  see cardLastFirst
 * @returns {null | {refused: false, from, to, kind} | {refused: true, to: string, message: string}}
 *   null            nothing to do (no card, an unknown side, or the card is already there — a reorder)
 *   refused         a seat into a room that holds someone else: `message` is the toast text (roomDropRefusal)
 *   from / to / kind  a real move, as resolveDragMove reports it
 */
export function planMove({ card, to, rooms } = {}, nameOf) {
  if (!card || typeof card !== 'object') return null;
  const move = resolveDragMove({ from: cardLocation(card), to });
  if (!move) return null;

  if (move.kind === 'seat') {
    const list = Array.isArray(rooms) ? rooms : [];
    const room = list.find((r) => r && String(r.opNum) === String(move.to));
    const message = room ? roomDropRefusal(room.cards, card.patNum, room.label, nameOf) : null;
    if (message) return { refused: true, to: String(move.to), message };
  }
  return { refused: false, from: move.from, to: move.to, kind: move.kind };
}
